import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { checkResetToken, resetPassword } from '../api/admin';
import { MIN_PASSWORD, passwordPairError, setKnownMinPassword } from '../components/PasswordDialogs';
import { AuthCard, initialsOf } from './AuthLayout';

/**
 * Where a reset link — or an invitation — lands.
 *
 * One page for both, because what happens here is identical: a single-use
 * token is spent and a password is written. Only the words differ, and they
 * have to: somebody who has just been added to a workspace is not "choosing a
 * NEW password", they are setting their first one, and a page that told them
 * their old password still works until they use the link would be describing
 * a password they have never had.
 *
 * The link is checked before the form appears rather than after it is
 * submitted: an expired link should say so while the person still has an empty
 * form, not after they have chosen and typed a password twice. Checking is a
 * read — spending the link is what the submit does, exactly once.
 *
 * R13 (ABC5-07): one small card. An invitation greets the person with the
 * workspace's tile and name and who added them, when the link says (the
 * invitation email already did); the new password shows how long it is.
 */
type Kind = 'reset' | 'invite';

interface Greeting { accountName?: string | null; invitedBy?: string | null; role?: string | null }

type State =
  | { kind: 'checking' }
  | { kind: 'ready'; link: Kind; email: string; greeting: Greeting; minPassword: number }
  | { kind: 'dead'; error: string }
  | { kind: 'done'; link: Kind; email: string };

const ROLE_WORD: Record<string, string> = { owner: 'an Owner', admin: 'an Admin', member: 'a Member' };

/**
 * How long the password is, in the words ABC5-07 draws under the field. It
 * measures length only, because length is what the server enforces
 * (MIN_PASSWORD) — it never claims more than that.
 */
export function passwordStrength(value: string, min: number = MIN_PASSWORD): { level: 0 | 1 | 2 | 3 | 4; label: string } {
  const n = value.length;
  // Nothing to say before the first keystroke: the sentence above the field
  // already gives the minimum.
  if (n === 0) return { level: 0, label: '' };
  if (n < min) return { level: 1, label: `Too short · ${min} characters minimum` };
  if (n < Math.max(12, min + 4)) return { level: 2, label: `Long enough · ${min}+ characters` };
  if (n < 16) return { level: 3, label: 'Strong · 12+ characters' };
  return { level: 4, label: 'Very strong · 16+ characters' };
}

function StrengthMeter({ value, min }: { value: string; min: number }) {
  const s = passwordStrength(value, min);
  return (
    <>
      <div className="auth-meter" data-level={s.level} aria-hidden="true"><span /><span /><span /><span /></div>
      <div className="auth-hint" aria-live="polite">{s.label}</div>
    </>
  );
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState<State>({ kind: 'checking' });
  const [pw, setPw] = useState({ value: '', confirm: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setState({ kind: 'dead', error: 'That link is missing its token. Ask for a new one.' });
      return;
    }
    checkResetToken(token)
      .then((r) => {
        // The minimum this link's password must meet (Platform › Rules), said
        // by the server that will check it; the code's number stands in.
        setKnownMinPassword(r.minPassword);
        setState({
          kind: 'ready', link: r.kind, email: r.email,
          greeting: { accountName: r.accountName, invitedBy: r.invitedBy, role: r.role },
          minPassword: r.minPassword ?? MIN_PASSWORD,
        });
      })
      .catch((e) => setState({ kind: 'dead', error: e.message || 'That link is no longer valid.' }));
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const pairError = passwordPairError(pw.value, pw.confirm);
    if (pairError) { setError(pairError); return; }
    setError(''); setBusy(true);
    try {
      const r = await resetPassword(token, pw.value);
      setState({ kind: 'done', link: r.kind, email: r.email });
    } catch (err: any) {
      setError(err.message || 'Could not set the password');
    } finally {
      setBusy(false);
    }
  }

  if (state.kind === 'checking') {
    return <AuthCard><p className="auth-lede">Checking the link…</p></AuthCard>;
  }

  /**
   * The dead end.
   *
   * It cannot say which kind of link expired — an expired token is a token
   * nothing knows anything about, which is the property that makes it safe —
   * so the copy covers both: ask for a new one, or ask whoever added you.
   */
  if (state.kind === 'dead') {
    return (
      <AuthCard>
        <h1 className="auth-title">This link has expired</h1>
        <div className="auth-err" role="alert">{state.error}</div>
        <p className="auth-lede">
          These links work once. Nothing has changed about your sign-in — ask for a new one and
          use that, or if you were invited, ask whoever added you to send the invitation again.
        </p>
        <Link className="fs-btn primary lg block" to="/forgot-password">Send me a new link</Link>
      </AuthCard>
    );
  }

  if (state.kind === 'done') {
    return (
      <AuthCard>
        <h1 className="auth-title">{state.link === 'invite' ? 'Your account is ready' : 'Password set'}</h1>
        <p className="auth-lede">
          <b>{state.email}</b> signs in with the password you just chose.
          {state.link === 'invite'
            ? ' Nobody else knows it — not even whoever added you.'
            : ' Every other browser that was signed in as you has been signed out.'}
        </p>
        <button className="fs-btn ink lg block" type="button" onClick={() => navigate('/login')}>
          Sign in
        </button>
      </AuthCard>
    );
  }

  const invited = state.link === 'invite';
  const { accountName, invitedBy, role } = state.greeting;
  return (
    <AuthCard>
      <form className="auth-stack" onSubmit={onSubmit}>
        {invited && accountName && <span className="auth-initials" aria-hidden="true">{initialsOf(accountName)}</span>}
        <h1 className="auth-title">
          {invited ? (accountName ? `Join ${accountName}` : 'Set up your account') : 'Choose a new password'}
        </h1>
        <p className="auth-lede">
          {invited
            ? (accountName
              ? <>{invitedBy ? <>{invitedBy} added <b>{state.email}</b> to</> : <><b>{state.email}</b> has been added to</>} {accountName}
                {role && ROLE_WORD[role] ? ` as ${ROLE_WORD[role]}` : ''}. Choose a password — at least {state.minPassword} characters — and you are in.</>
              : <>You have been added to Form-Magic as <b>{state.email}</b>. Choose a password — at least {state.minPassword} characters — and you are in.</>)
            : <>Setting the password for <b>{state.email}</b>. At least {state.minPassword} characters.</>}
        </p>
        {error && <div className="auth-err" role="alert">{error}</div>}
        {/* The username field is hidden but present so a password manager
            files the new password under the right account. */}
        <input type="text" name="username" autoComplete="username" value={state.email}
          readOnly hidden aria-hidden="true" />
        <div className="auth-field">
          <label htmlFor="reset-new">{invited ? 'Your password' : 'New password'}</label>
          <input id="reset-new" type="password" autoFocus autoComplete="new-password" value={pw.value}
            onChange={(e) => setPw({ ...pw, value: e.target.value })} />
          <StrengthMeter value={pw.value} min={state.minPassword} />
        </div>
        <div className="auth-field">
          <label htmlFor="reset-repeat">Repeat it</label>
          <input id="reset-repeat" type="password" autoComplete="new-password" value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
        </div>
        <button className="fs-btn primary lg block" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Set password and sign in'}
        </button>
      </form>
    </AuthCard>
  );
}
